import { messaging, getToken, onMessage } from '../config/firebase';
import { apiRegisterDevice } from '../api/device';

class FirebaseMessagingService {
    constructor() {
        this.fcmToken = null;
        this.isSupported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'Notification' in window;
        this.serviceWorkerRegistration = null;
    }

    async requestPermission() {
        if (!this.isSupported) {
            console.warn('Notifications not supported in this browser');
            return null;
        }

        try {
            // Ensure service worker is registered before requesting permission
            await this.registerServiceWorker();

            const permission = await Notification.requestPermission();

            if (permission === 'granted') {
                const token = await this.getFCMToken();
                return token;
            } else if (permission === 'denied') {
                console.warn('Notification permission denied by user');
                return null;
            } else {
                console.warn('Notification permission dismissed by user');
                return null;
            }
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return null;
        }
    }

    async registerServiceWorker() {
        if (!this.isSupported) {
            console.warn('Service worker not supported');
            return null;
        }

        if (this.serviceWorkerRegistration) {
            try {
                await navigator.serviceWorker.ready;
                if (this.serviceWorkerRegistration.active) {
                    return this.serviceWorkerRegistration;
                }
                this.serviceWorkerRegistration = null;
            } catch (error) {
                console.error('Error waiting for service worker ready:', error);
                this.serviceWorkerRegistration = null;
                return null;
            }
        }

        try {
            const existingRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js') 
                || await navigator.serviceWorker.getRegistration('/');
            
            if (existingRegistration) {
                this.serviceWorkerRegistration = existingRegistration;
                await existingRegistration.ready;
                return existingRegistration;
            }

            // Register new service worker
            const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                scope: '/'
            });
            this.serviceWorkerRegistration = registration;
            
            // Wait for service worker to be ready (activated) using the ready promise
            await registration.ready;
            
            return registration;
        } catch (error) {
            console.error('Service Worker registration failed:', error);
            return null;
        }
    }

    async getFCMToken() {
        if (!messaging) {
            console.error('Firebase messaging not initialized');
            return null;
        }

        const registration = await this.registerServiceWorker();
        if (!registration || !registration.active) {
            console.error('Service Worker is not active');
            return null;
        }

        if (registration.active.state === 'activating') {
            console.log('[FirebaseMessaging] Service Worker is activating, waiting for activation...');
            await new Promise((resolve) => {
                const timeout = setTimeout(resolve, 3000);
                const checkState = () => {
                    if (registration.active && registration.active.state === 'activated') {
                        clearTimeout(timeout);
                        registration.active.removeEventListener('statechange', checkState);
                        resolve();
                    }
                };
                if (registration.active) {
                    registration.active.addEventListener('statechange', checkState);
                } else {
                    clearTimeout(timeout);
                    resolve();
                }
            });
        }
        
        try {
            const vapidKey = process.env.REACT_APP_FIREBASE_WEB_NOTIFICATION_VAPID_KEY;

            if (!vapidKey) {
                console.error('VAPID key is not set');
                return null;
            }

            const token = await getToken(messaging, { 
                vapidKey,
                serviceWorkerRegistration: registration
            });

            if (token) {
                this.fcmToken = token;
                return token;
            } else {
                return null;
            }
        } catch (error) {
            console.error('An error occurred while retrieving token:', error);
            return null;
        }
    }

    getStoredFCMToken() {
        return this.fcmToken;
    }

    clearFCMToken() {
        this.fcmToken = null;
    }

    getDeviceId() {
        let deviceId = localStorage.getItem('deviceId');
        
        if (!deviceId) {
          const browser = this.getBrowserName();
          const platform = navigator.platform || 'unknown';
          const uniqueId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          deviceId = `${browser}-${platform}-${uniqueId}`;
          localStorage.setItem('deviceId', deviceId);
        }
        
        return deviceId;
    }

    getBrowserName() {
        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.includes('chrome')) return 'chrome';
        if (userAgent.includes('firefox')) return 'firefox';
        if (userAgent.includes('safari')) return 'safari';
        if (userAgent.includes('edge')) return 'edge';
        return 'browser';
    }

    async registerDevice() {
        try {
            const token = this.fcmToken || await this.getFCMToken();
          
            if (!token) {
                console.warn('No FCM token available for registration');
                return false;
            }
          
            const deviceData = {
                device_id: this.getDeviceId(),
                device_type: 3,
                fcm_token: token
            };
    
            const response = await apiRegisterDevice(deviceData);
          
            if (response) {
                return true;
            }
          
            return false;
        } catch (error) {
            console.error('Error registering device:', error);
            return false;
        }
    }

    onMessage(callback) {
        if (!messaging) {
            console.warn('Firebase messaging not initialized');
            return;
        }
      
        onMessage(messaging, (payload) => {            
            if (callback) {
              callback(payload);
            }
        });
    }

    isNotificationSupported() {
        return this.isSupported;
    }

    getPermissionStatus() {
        if (!this.isNotificationSupported()) {
            return 'not-supported';
        }
        return Notification.permission;
    }

    async showNativeNotification(payload) {
        if (!this.isSupported || Notification.permission !== 'granted') {
            console.warn('[FirebaseMessaging] Cannot show notification - permission not granted');
            return;
        }

        try {
            const registration = await this.registerServiceWorker();
            if (!registration) {
                console.error('[FirebaseMessaging] Service Worker not available for showing notification');
                return;
            }

            const notification = payload.notification || {};
            const data = payload.data || {};

            const notificationTitle = notification.title || 'New Notification';
            const notificationBody = notification.body || data.message || '';
            const notificationIcon = notification.icon || '/logo192.png';
            const notificationImage = notification.image;

            const notificationOptions = {
                body: notificationBody,
                icon: notificationIcon,
                badge: '/logo192.png',
                image: notificationImage,
                data: data, // Pass all data for navigation
                tag: data.data_id || data.notificationId || `notification-${Date.now()}`,
                requireInteraction: false,
                timestamp: data.timestamp ? parseInt(data.timestamp) * 1000 : Date.now(),
                actions: [
                    {
                        action: 'open',
                        title: 'Open'
                    },
                    {
                        action: 'close',
                        title: 'Close'
                    }
                ],
                vibrate: [200, 100, 200], // Vibration pattern
                silent: false
            };

            // Show notification using service worker
            await registration.showNotification(notificationTitle, notificationOptions);
            console.log('[FirebaseMessaging] ✅ Native notification shown');
        } catch (error) {
            console.error('[FirebaseMessaging] ❌ Error showing native notification:', error);
        }
    }
}

const firebaseMessagingService = new FirebaseMessagingService();
export default firebaseMessagingService;